// Generates a tasteful SVG cover image as a data URI from a topic + subtitle.
// Mocks what an image-gen tool (DALL-E / Nano Banana) would produce. Shared
// across every social platform asset within a single agent run (no need to
// generate a unique image per platform — platforms only need a re-crop).

const PALETTES = [
  { bg1: "#070B16", bg2: "#142447", accent1: "#8FE04A", accent2: "#2D6BFF", text: "#EAF1FB" },
  { bg1: "#0A0712", bg2: "#33133A", accent1: "#F6B24A", accent2: "#FF5A6A", text: "#FFEFD1" },
  { bg1: "#06120E", bg2: "#0F3A2D", accent1: "#65D993", accent2: "#8FE04A", text: "#E1FFEF" },
  { bg1: "#0E0716", bg2: "#3A1462", accent1: "#A66BFF", accent2: "#8FE04A", text: "#F2E8FF" },
];

function pickPalette(seed = "") {
  let h = 0;
  for (let i = 0; i < seed.length; i++) h = (h * 31 + seed.charCodeAt(i)) >>> 0;
  return PALETTES[h % PALETTES.length];
}

function escapeSvg(s = "") {
  return s
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&apos;");
}

function wrap(text, maxChars) {
  const words = (text || "").trim().split(/\s+/);
  const lines = [];
  let cur = "";
  for (const w of words) {
    if ((cur + " " + w).trim().length > maxChars) {
      if (cur) lines.push(cur);
      cur = w;
    } else {
      cur = (cur + " " + w).trim();
    }
  }
  if (cur) lines.push(cur);
  return lines.slice(0, 3);
}

export function generateCoverImage({ title = "", subtitle = "", seed = "" } = {}) {
  const p = pickPalette(seed || title);
  const titleLines = wrap(title, 22);
  const titleSvg = titleLines
    .map((l, i) => `<tspan x="64" dy="${i === 0 ? 0 : 86}">${escapeSvg(l)}</tspan>`)
    .join("");
  const w = 1200;
  const h = 675;
  const svg = `<?xml version="1.0" encoding="UTF-8"?>
<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 ${w} ${h}" width="${w}" height="${h}">
  <defs>
    <linearGradient id="bg" x1="0" y1="0" x2="1" y2="1">
      <stop offset="0%" stop-color="${p.bg1}"/>
      <stop offset="100%" stop-color="${p.bg2}"/>
    </linearGradient>
    <radialGradient id="g1" cx="0.18" cy="0.22" r="0.55">
      <stop offset="0%" stop-color="${p.accent1}" stop-opacity="0.45"/>
      <stop offset="100%" stop-color="${p.accent1}" stop-opacity="0"/>
    </radialGradient>
    <radialGradient id="g2" cx="0.85" cy="0.85" r="0.6">
      <stop offset="0%" stop-color="${p.accent2}" stop-opacity="0.38"/>
      <stop offset="100%" stop-color="${p.accent2}" stop-opacity="0"/>
    </radialGradient>
    <filter id="grain"><feTurbulence type="fractalNoise" baseFrequency="0.9" numOctaves="2" stitchTiles="stitch"/><feColorMatrix values="0 0 0 0 1  0 0 0 0 1  0 0 0 0 1  0 0 0 0.06 0"/></filter>
  </defs>
  <rect width="${w}" height="${h}" fill="url(#bg)"/>
  <rect width="${w}" height="${h}" fill="url(#g1)"/>
  <rect width="${w}" height="${h}" fill="url(#g2)"/>
  <rect width="${w}" height="${h}" filter="url(#grain)" opacity="0.35"/>
  <g transform="translate(0,0)">
    <rect x="64" y="64" width="86" height="36" rx="18" fill="${p.accent1}" fill-opacity="0.18" stroke="${p.accent1}" stroke-opacity="0.45" stroke-width="1"/>
    <text x="107" y="88" text-anchor="middle" font-family="Inter, Helvetica, Arial, sans-serif" font-weight="700" font-size="14" letter-spacing="2" fill="${p.accent1}">ADAMS AI</text>
  </g>
  <text font-family="Inter, Helvetica, Arial, sans-serif" font-weight="700" font-size="72" fill="${p.text}" letter-spacing="-1">
    <tspan x="64" y="${h / 2 - (titleLines.length - 1) * 43 + 16}">${titleSvg || escapeSvg(title)}</tspan>
  </text>
  <text x="64" y="${h - 64}" font-family="Inter, Helvetica, Arial, sans-serif" font-weight="500" font-size="22" fill="${p.text}" fill-opacity="0.7">${escapeSvg(subtitle)}</text>
  <g transform="translate(${w - 220}, ${h - 88})">
    <rect width="156" height="24" rx="12" fill="black" fill-opacity="0.35"/>
    <circle cx="14" cy="12" r="3" fill="${p.accent1}"/>
    <text x="26" y="16" font-family="Inter, Helvetica, Arial, sans-serif" font-weight="600" font-size="11" letter-spacing="2" fill="${p.text}">AI · GENERATED</text>
  </g>
</svg>`;
  return `data:image/svg+xml;utf8,${encodeURIComponent(svg)}`;
}
